import { 
  Gauge, Clock, Eye, AlertTriangle, Heart, Shield, Car, Compass, Truck, Users,
  Lightbulb, Phone, Fuel, Thermometer, CloudRain, Moon, Sun, Map, Wrench, Battery
} from 'lucide-react';

// Icon mapping for dynamic icon rendering
const iconMap = {
  Gauge, Clock, Eye, AlertTriangle, Heart, Shield, Car, Compass, Truck, Users,
  Lightbulb, Phone, Fuel, Thermometer, CloudRain, Moon, Sun, Map, Wrench, Battery
};

// Helper function to get icon component from string name
export const getIconComponent = (iconName) => {
  return iconMap[iconName] || Lightbulb;
};

export const categories = [
  { id: 'all', label: { ar: 'الكل', sv: 'Alla', en: 'All' }, icon: '📋' },
  { id: 'speed', label: { ar: 'السرعة', sv: 'Hastighet', en: 'Speed' }, icon: '🚀' },
  { id: 'safety', label: { ar: 'السلامة', sv: 'Säkerhet', en: 'Safety', tr: 'Güvenlik', fa: 'ایمنی', so: 'Badbaado', pl: 'Bezpieczeństwo', es: 'Seguridad', de: 'Sicherheit' }, icon: '🛡️' },
  { id: 'rules', label: { ar: 'القواعد', sv: 'Regler', en: 'Rules', tr: 'Kurallar', fa: 'قوانین', so: 'Xeerarka', pl: 'Przepisy', es: 'Reglas', de: 'Regeln' }, icon: '📜' },
  { id: 'signs', label: { ar: 'الإشارات', sv: 'Skyltar', en: 'Signs', tr: 'İşaretler', fa: 'علائم', so: 'Calaamadaha', pl: 'Znaki', es: 'Señales', de: 'Schilder' }, icon: '🚦' },
  { id: 'weather', label: { ar: 'الطقس', sv: 'Väder', en: 'Weather', tr: 'Hava', fa: 'آب و هوا', so: 'Cimilada', pl: 'Pogoda', es: 'Clima', de: 'Wetter' }, icon: '🌧️' },
  { id: 'parking', label: { ar: 'الوقوف', sv: 'Parkering', en: 'Parking', tr: 'Park', fa: 'پارکینگ', so: 'Baarkinka', pl: 'Parkowanie', es: 'Aparcamiento', de: 'Parken' }, icon: '🅿️' },
  { id: 'exam', label: { ar: 'الاختبار', sv: 'Provet', en: 'Exam', tr: 'Sınav', fa: 'آزمون', so: 'Imtixaanka', pl: 'Egzamin', es: 'Examen', de: 'Prüfung' }, icon: '✍️' },
  { id: 'vehicle', label: { ar: 'السيارة', sv: 'Fordonet', en: 'Vehicle', tr: 'Araç', fa: 'خودرو', so: 'Gaariga', pl: 'Pojazd', es: 'Vehículo', de: 'Fahrzeug' }, icon: '🚗' },
  { id: 'eco', label: { ar: 'بيئي', sv: 'Miljö', en: 'Eco', tr: 'Çevre', fa: 'زیست محیطی', so: 'Deegaanka', pl: 'Ekologia', es: 'Ecología', de: 'Umwelt' }, icon: '🌱' },
];

export const tips = [
  {
    id: 1,
    category: 'speed',
    icon: Gauge,
    color: 'bg-blue-500',
    title: { ar: 'حدود السرعة في السويد', sv: 'Hastighetsgränser i Sverige', en: 'Speed Limits in Sweden' },
    content: {
      ar: `الحدود القياسية:\n• المناطق السكنية: 50 كم/س\n• مناطق المدارس: 30 كم/س\n• الطرق الريفية: 70-90 كم/س\n• الطرق السريعة: 110-120 كم/س`,
      sv: `Standardgränser:\n• Tätort: 50 km/h\n• Skolområden: 30 km/h\n• Landsväg: 70-90 km/h\n• Motorväg: 110-120 km/h`,
      en: `Standard limits:\n• Urban areas: 50 km/h\n• School zones: 30 km/h\n• Rural roads: 70-90 km/h\n• Highways: 110-120 km/h`
    }
  },
  {
    id: 2,
    category: 'speed',
    icon: Clock,
    color: 'bg-purple-500',
    title: { ar: 'قاعدة الـ 3 ثواني', sv: '3-sekundersregeln', en: '3-Second Rule' },
    content: {
      ar: `كيف تطبق القاعدة:\n1. اختر نقطة ثابتة\n2. عد عندما تمر السيارة الأمامية\n3. إذا وصلت قبل 3 ثواني = قريب جداً!\n\nفي الظروف السيئة: 4-8 ثواني`,
      sv: `Så här tillämpar du:\n1. Välj en fast punkt\n2. Räkna när bilen framför passerar\n3. Om du når innan 3 sek = för nära!\n\nVid dåliga förhållanden: 4-8 sek`,
      en: `How to apply:\n1. Pick a fixed point\n2. Count when car ahead passes\n3. If you reach before 3 sec = too close!\n\nIn bad conditions: 4-8 seconds`
    }
  },
  {
    id: 3,
    category: 'speed',
    icon: Gauge,
    color: 'bg-indigo-500',
    title: { ar: 'مسافة التوقف', sv: 'Stoppsträcka', en: 'Stopping Distance' },
    content: {
      ar: `مسافة التوقف = رد الفعل + الفرملة\n\n• 50 كم/س = 27 متر\n• 70 كم/س = 44 متر\n• 90 كم/س = 65 متر\n\nفي الطريق المبلل: ضاعف المسافة!`,
      sv: `Stoppsträcka = reaktion + bromsning\n\n• 50 km/h = 27 meter\n• 70 km/h = 44 meter\n• 90 km/h = 65 meter\n\nPå våt väg: dubbla!`,
      en: `Stopping = reaction + braking\n\n• 50 km/h = 27 meters\n• 70 km/h = 44 meters\n• 90 km/h = 65 meters\n\nOn wet road: double it!`
    }
  },
  {
    id: 4,
    category: 'safety',
    icon: Eye,
    color: 'bg-green-500',
    title: { ar: 'تقنية المسح البصري', sv: 'Avsökningsteknik', en: 'Visual Scanning' },
    content: {
      ar: `قاعدة 5-8-12:\n• المرايا: كل 5-8 ثواني\n• النظر للأمام: 12-15 ثانية\n\nتحقق من النقطة العمياء:\n• قبل تغيير المسار\n• قبل الانعطاف\n• قبل الدوار`,
      sv: `5-8-12 regeln:\n• Speglar: var 5-8 sek\n• Titta framåt: 12-15 sek\n\nKontrollera döda vinkeln:\n• Före filbyte\n• Före sväng\n• Före rondell`,
      en: `5-8-12 rule:\n• Mirrors: every 5-8 sec\n• Look ahead: 12-15 sec\n\nCheck blind spot:\n• Before lane change\n• Before turning\n• Before roundabout`
    }
  },
  {
    id: 5,
    category: 'safety',
    icon: AlertTriangle,
    color: 'bg-red-500',
    title: { ar: 'حالات الطوارئ', sv: 'Nödsituationer', en: 'Emergencies' },
    content: {
      ar: `انفجار الإطار:\n• أمسك المقود بقوة\n• لا تفرمل فجأة!\n• ارفع قدمك عن البنزين\n\nالانزلاق:\n• ارفع قدمك عن البنزين\n• وجه المقود في اتجاه الانزلاق`,
      sv: `Punktering:\n• Håll ratten stadigt\n• Bromsa inte plötsligt!\n• Släpp gasen\n\nSladd:\n• Släpp gasen\n• Styr i sladdningens riktning`,
      en: `Tire blowout:\n• Hold wheel firmly\n• Don't brake suddenly!\n• Ease off gas\n\nSkidding:\n• Release gas\n• Steer in skid direction`
    }
  },
  {
    id: 6,
    category: 'safety',
    icon: Heart,
    color: 'bg-pink-500',
    title: { ar: 'الإسعافات الأولية', sv: 'Första hjälpen', en: 'First Aid' },
    content: {
      ar: `خطوات الحادث:\n1. تأمين المكان + مثلث التحذير\n2. اتصل بـ 112\n3. ساعد المصابين\n4. لا تحرك المصاب إلا للضرورة`,
      sv: `Steg vid olycka:\n1. Säkra platsen + varningstriangel\n2. Ring 112\n3. Hjälp de skadade\n4. Flytta inte skadade om inte nödvändigt`,
      en: `Accident steps:\n1. Secure scene + warning triangle\n2. Call 112\n3. Help the injured\n4. Don't move injured unless necessary`
    }
  },
  {
    id: 7,
    category: 'safety',
    icon: Shield,
    color: 'bg-teal-500',
    title: { ar: 'حزام الأمان والأطفال', sv: 'Bälte och barn', en: 'Seatbelt & Children' },
    content: {
      ar: `قواعد حزام الأمان:\n• إجباري للجميع\n• السائق مسؤول عن < 15 سنة\n• الغرامة: 1500 كرونة\n\nالأطفال < 135 سم: كرسي مناسب`,
      sv: `Bältesregler:\n• Obligatoriskt för alla\n• Föraren ansvarar för < 15 år\n• Böter: 1500 kr\n\nBarn < 135 cm: lämplig stol`,
      en: `Seatbelt rules:\n• Mandatory for all\n• Driver responsible for < 15 yrs\n• Fine: 1500 SEK\n\nChildren < 135 cm: proper seat`
    }
  },
  {
    id: 8,
    category: 'rules',
    icon: Car,
    color: 'bg-amber-500',
    title: { ar: 'قواعد الدوار', sv: 'Rondellregler', en: 'Roundabout Rules' },
    content: {
      ar: `عند الدخول:\n• أبطئ وانظر يساراً\n• لا تحتاج إشارة\n\nعند الخروج:\n• استخدم إشارة اليمين دائماً!\n\nمن داخل الدوار = له الأولوية`,
      sv: `Vid infart:\n• Sakta ner, titta vänster\n• Behöver inte blinka\n\nVid utfart:\n• Använd alltid höger blinkers!\n\nDen i rondellen = har företräde`,
      en: `When entering:\n• Slow down, look left\n• No signal needed\n\nWhen exiting:\n• Always use right signal!\n\nInside roundabout = has priority`
    }
  },
  {
    id: 9,
    category: 'rules',
    icon: Compass,
    color: 'bg-orange-500',
    title: { ar: 'قاعدة اليمين', sv: 'Högerregeln', en: 'Right-Hand Rule' },
    content: {
      ar: `تطبق في تقاطعات بدون إشارات.\n\nالقاعدة: القادم من يمينك له الأولوية.\n\nلا تطبق:\n• عند إشارة ضوئية\n• عند STOP\n• على الطريق الرئيسي`,
      sv: `Gäller i korsningar utan skyltar.\n\nRegeln: Fordon från höger har företräde.\n\nGäller INTE:\n• Vid trafikljus\n• Vid STOPP\n• På huvudled`,
      en: `Applies at intersections without signs.\n\nRule: Vehicle from right has priority.\n\nDoes NOT apply:\n• At traffic lights\n• At STOP sign\n• On main road`
    }
  },
  {
    id: 10,
    category: 'rules',
    icon: Truck,
    color: 'bg-slate-600',
    title: { ar: 'التجاوز الآمن', sv: 'Säker omkörning', en: 'Safe Overtaking' },
    content: {
      ar: `قبل التجاوز:\n• تأكد الطريق واضح\n• لا خطوط متصلة\n• تحقق من المرايا\n\nممنوع التجاوز عند:\n• ممرات المشاة\n• التقاطعات\n• المنحنيات`,
      sv: `Före omkörning:\n• Kontrollera att vägen är fri\n• Inga heldragna linjer\n• Kolla speglarna\n\nFörbjudet vid:\n• Övergångsställen\n• Korsningar\n• Kurvor`,
      en: `Before overtaking:\n• Check road is clear\n• No solid lines\n• Check mirrors\n\nForbidden at:\n• Crosswalks\n• Intersections\n• Curves`
    }
  },
  {
    id: 11,
    category: 'rules',
    icon: Users,
    color: 'bg-violet-500',
    title: { ar: 'أولوية المشاة', sv: 'Fotgängares företräde', en: 'Pedestrian Priority' },
    content: {
      ar: `ممر المشاة:\n• المشاة لهم الأولوية دائماً\n• توقف إذا ينتظرون\n\nغرامة عدم الأولوية:\n3000-4000 كرونة`,
      sv: `Övergångsställe:\n• Fotgängare har alltid företräde\n• Stanna om de väntar\n\nBöter:\n3000-4000 kr`,
      en: `Crosswalk:\n• Pedestrians always have priority\n• Stop if they're waiting\n\nFine:\n3000-4000 SEK`
    }
  },
  {
    id: 12,
    category: 'signs',
    icon: AlertTriangle,
    color: 'bg-yellow-500',
    title: { ar: 'علامات التحذير', sv: 'Varningsskyltar', en: 'Warning Signs' },
    content: {
      ar: `الشكل: مثلث أحمر + أصفر\n\nأمثلة:\n• منحنى خطير\n• ممر مشاة\n• أطفال/مدرسة\n• طريق زلق\n\nماذا تفعل؟ خفف السرعة!`,
      sv: `Form: röd triangel + gul\n\nExempel:\n• Farlig kurva\n• Övergångsställe\n• Barn/skola\n• Hal väg\n\nVad gör du? Sänk hastigheten!`,
      en: `Shape: red triangle + yellow\n\nExamples:\n• Dangerous curve\n• Crosswalk\n• Children/school\n• Slippery road\n\nWhat to do? Reduce speed!`
    }
  },
  {
    id: 13,
    category: 'signs',
    icon: Shield,
    color: 'bg-red-600',
    title: { ar: 'علامات المنع', sv: 'Förbudsskyltar', en: 'Prohibition Signs' },
    content: {
      ar: `الشكل: دائرة حمراء + أبيض\n\nأمثلة:\n• ممنوع الدخول\n• ممنوع التجاوز\n• السرعة القصوى\n• ممنوع الوقوف`,
      sv: `Form: röd cirkel + vit\n\nExempel:\n• Förbud mot infart\n• Omkörningsförbud\n• Högsta hastighet\n• Parkeringsförbud`,
      en: `Shape: red circle + white\n\nExamples:\n• No entry\n• No overtaking\n• Speed limit\n• No parking`
    }
  },
  {
    id: 14,
    category: 'signs',
    icon: Compass,
    color: 'bg-blue-600',
    title: { ar: 'علامات الإرشاد', sv: 'Upplysningsskyltar', en: 'Information Signs' },
    content: {
      ar: `الشكل: مستطيل أزرق/أخضر\n\nأخضر = طريق سريع\nأزرق = طريق عادي\n\nأمثلة:\n• موقف سيارات P\n• محطة وقود\n• مستشفى H`,
      sv: `Form: blå/grön rektangel\n\nGrön = motorväg\nBlå = vanlig väg\n\nExempel:\n• Parkering P\n• Bensinstation\n• Sjukhus H`,
      en: `Shape: blue/green rectangle\n\nGreen = highway\nBlue = regular road\n\nExamples:\n• Parking P\n• Gas station\n• Hospital H`
    }
  },
  {
    id: 15,
    category: 'weather',
    icon: Thermometer,
    color: 'bg-cyan-500',
    title: { ar: 'القيادة في الشتاء', sv: 'Vinterkörning', en: 'Winter Driving' },
    content: {
      ar: `الإطارات الشتوية:\n• إجبارية: 1 ديسمبر - 31 مارس\n• عمق المداس: 3 مم\n\nنصائح:\n• ضاعف المسافة\n• فرمل برفق\n• تجنب الحركات المفاجئة`,
      sv: `Vinterdäck:\n• Obligatoriska: 1 dec - 31 mars\n• Mönsterdjup: 3 mm\n\nTips:\n• Dubbla avståndet\n• Bromsa mjukt\n• Undvik plötsliga rörelser`,
      en: `Winter tires:\n• Mandatory: Dec 1 - Mar 31\n• Tread depth: 3 mm\n\nTips:\n• Double the distance\n• Brake gently\n• Avoid sudden movements`
    }
  },
  {
    id: 16,
    category: 'weather',
    icon: CloudRain,
    color: 'bg-slate-500',
    title: { ar: 'القيادة في المطر', sv: 'Körning i regn', en: 'Driving in Rain' },
    content: {
      ar: `مخاطر:\n• انخفاض الرؤية\n• Aquaplaning\n\nنصائح:\n• شغل الأضواء\n• خفض السرعة 10-20 كم/س\n• زد المسافة\n\nأول 15 دقيقة هي الأخطر!`,
      sv: `Risker:\n• Försämrad sikt\n• Aquaplaning\n\nTips:\n• Tänd ljus\n• Sänk hastigheten 10-20 km/h\n• Öka avståndet\n\nFörsta 15 min är farligast!`,
      en: `Hazards:\n• Reduced visibility\n• Aquaplaning\n\nTips:\n• Turn on lights\n• Reduce speed 10-20 km/h\n• Increase distance\n\nFirst 15 min most dangerous!`
    }
  },
  {
    id: 17,
    category: 'weather',
    icon: Moon,
    color: 'bg-slate-700',
    title: { ar: 'القيادة في الليل', sv: 'Nattkörning', en: 'Night Driving' },
    content: {
      ar: `الأضواء:\n• المنخفضة: دائماً في الليل\n• العالية: فقط بدون سيارات\n\nغير للمنخفضة عند:\n• سيارة قادمة\n• سيارة أمامك\n• في المدينة`,
      sv: `Ljus:\n• Halvljus: alltid på natten\n• Helljus: endast utan bilar\n\nByt till halvljus vid:\n• Mötande bil\n• Bil framför\n• I tätort`,
      en: `Lights:\n• Low beams: always at night\n• High beams: only without cars\n\nSwitch to low when:\n• Oncoming car\n• Car ahead\n• In urban areas`
    }
  },
  {
    id: 18,
    category: 'weather',
    icon: Sun,
    color: 'bg-orange-400',
    title: { ar: 'الضباب والشمس', sv: 'Dimma och sol', en: 'Fog and Sun' },
    content: {
      ar: `الضباب:\n• أضواء منخفضة (ليس عالية!)\n• أضواء ضباب خلفية: رؤية < 50م\n\nالشمس:\n• نظارات شمسية\n• نظف الزجاج`,
      sv: `Dimma:\n• Halvljus (inte helljus!)\n• Dimljus bak: sikt < 50m\n\nSol:\n• Solglasögon\n• Rengör vindrutan`,
      en: `Fog:\n• Low beams (not high!)\n• Rear fog lights: visibility < 50m\n\nSun:\n• Sunglasses\n• Clean windshield`
    }
  },
  {
    id: 19,
    category: 'parking',
    icon: Map,
    color: 'bg-emerald-500',
    title: { ar: 'قواعد الوقوف', sv: 'Parkeringsregler', en: 'Parking Rules' },
    content: {
      ar: `ممنوع الوقوف:\n• على ممر المشاة\n• قبل التقاطع 10م\n• على الرصيف\n• أمام المداخل\n\nالخط الأصفر المتصل = ممنوع`,
      sv: `Parkering förbjuden:\n• På övergångsställe\n• Före korsning 10m\n• På trottoaren\n• Framför infarter\n\nGul heldragen linje = förbjudet`,
      en: `Parking forbidden:\n• On crosswalk\n• Before intersection 10m\n• On sidewalk\n• In front of entrances\n\nSolid yellow line = forbidden`
    }
  },
  {
    id: 20,
    category: 'parking',
    icon: Gauge,
    color: 'bg-lime-500',
    title: { ar: 'المسافات الإلزامية', sv: 'Obligatoriska avstånd', en: 'Required Distances' },
    content: {
      ar: `قبل ممر المشاة: 10م\nقبل التقاطع: 10م\nمن محطة الحافلات: 20م\nمن زاوية الشارع: 10م`,
      sv: `Före övergångsställe: 10m\nFöre korsning: 10m\nFrån busshållplats: 20m\nFrån gathörn: 10m`,
      en: `Before crosswalk: 10m\nBefore intersection: 10m\nFrom bus stop: 20m\nFrom street corner: 10m`
    }
  },
  {
    id: 21,
    category: 'vehicle',
    icon: Wrench,
    color: 'bg-gray-600',
    title: { ar: 'فحص السيارة', sv: 'Fordonskontroll', en: 'Vehicle Check' },
    content: {
      ar: `قبل الرحلة:\n• الإطارات (ضغط + مداس)\n• الزيت والسوائل\n• الأضواء\n• المرايا\n• المساحات`,
      sv: `Före resan:\n• Däck (tryck + mönster)\n• Olja och vätskor\n• Lampor\n• Speglar\n• Vindrutetorkare`,
      en: `Before trip:\n• Tires (pressure + tread)\n• Oil and fluids\n• Lights\n• Mirrors\n• Wipers`
    }
  },
  {
    id: 22,
    category: 'vehicle',
    icon: Battery,
    color: 'bg-yellow-600',
    title: { ar: 'أضواء التحذير', sv: 'Varningslampor', en: 'Warning Lights' },
    content: {
      ar: `أحمر = توقف فوراً!\n• حرارة المحرك\n• ضغط الزيت\n• الفرامل\n\nأصفر = انتبه\n• فحص المحرك\n• البطارية`,
      sv: `Röd = stanna direkt!\n• Motortemperatur\n• Oljetryck\n• Bromsar\n\nGul = var uppmärksam\n• Motorkontroll\n• Batteri`,
      en: `Red = stop immediately!\n• Engine temp\n• Oil pressure\n• Brakes\n\nYellow = pay attention\n• Engine check\n• Battery`
    }
  },
  {
    id: 23,
    category: 'exam',
    icon: Lightbulb,
    color: 'bg-teal-500',
    title: { ar: 'نصائح يوم الاختبار', sv: 'Tips för provdagen', en: 'Exam Day Tips' },
    content: {
      ar: `قبل الاختبار:\n• نم 7-8 ساعات\n• فطور خفيف\n• اصل مبكراً 30 دقيقة\n\nمعلومات:\n• 70 سؤالاً (يُحسب منها 65)\n• تحتاج 52 للنجاح (80%)\n• 50 دقيقة`,
      sv: `Före provet:\n• Sov 7-8 timmar\n• Lätt frukost\n• Kom 30 min tidigt\n\nInfo:\n• 70 frågor (65 räknas)\n• Behöver 52 för att klara (80%)\n• 50 minuter`,
      en: `Before exam:\n• Sleep 7-8 hours\n• Light breakfast\n• Arrive 30 min early\n\nInfo:\n• 70 questions (65 scored)\n• Need 52 to pass (80%)\n• 50 minutes`
    }
  },
  {
    id: 24,
    category: 'exam',
    icon: Phone,
    color: 'bg-rose-500',
    title: { ar: 'الهاتف والقيادة', sv: 'Mobilen och körning', en: 'Phone and Driving' },
    content: {
      ar: `ممنوع استخدام الهاتف باليد!\n\nمسموح:\n• بلوتوث\n• نظام السيارة\n\nالغرامة: 1500 كرونة`,
      sv: `Förbjudet att använda mobilen i handen!\n\nTillåtet:\n• Bluetooth\n• Bilens system\n\nBöter: 1500 kr`,
      en: `Forbidden to use handheld phone!\n\nAllowed:\n• Bluetooth\n• Car system\n\nFine: 1500 SEK`
    }
  },
  {
    id: 25,
    category: 'exam',
    icon: Fuel,
    color: 'bg-amber-600',
    title: { ar: 'الكحول والمخدرات', sv: 'Alkohol och droger', en: 'Alcohol and Drugs' },
    content: {
      ar: `الحد القانوني: 0.2 ‰\n\nالعقوبات:\n• 0.2-1.0: غرامة + سحب رخصة\n• > 1.0: سجن\n\nالوقت هو الحل الوحيد!`,
      sv: `Laglig gräns: 0.2 ‰\n\nStraff:\n• 0.2-1.0: böter + indragning\n• > 1.0: fängelse\n\nTid är enda lösningen!`,
      en: `Legal limit: 0.2 ‰\n\nPenalties:\n• 0.2-1.0: fine + suspension\n• > 1.0: prison\n\nTime is the only solution!`
    }
  },
  {
    id: 26,
    category: 'rules',
    icon: Car,
    color: 'bg-sky-500',
    title: { ar: 'الطريق السريع', sv: 'Motorvägen', en: 'Highway' },
    content: {
      ar: `الدخول:\n• استخدم مسار التسارع\n• أشر يساراً\n\nالقيادة:\n• اليمين للعادية\n• اليسار للتجاوز فقط\n\nالحد الأدنى: 40 كم/س`,
      sv: `Infart:\n• Använd accelerationsfältet\n• Blinka vänster\n\nKörning:\n• Höger för normal\n• Vänster endast för omkörning\n\nMinsta: 40 km/h`,
      en: `Entering:\n• Use acceleration lane\n• Signal left\n\nDriving:\n• Right for normal\n• Left for overtaking only\n\nMinimum: 40 km/h`
    }
  },
  {
    id: 27,
    category: 'eco',
    icon: Fuel,
    color: 'bg-green-600',
    title: { ar: 'القيادة الاقتصادية', sv: 'Ecodriving', en: 'Eco-Driving' },
    content: {
      ar: `توفير 10-30% وقود:\n• تسارع برفق\n• سرعات عالية مبكراً\n• سرعة ثابتة\n• أطفئ المحرك > 30 ثانية`,
      sv: `Spara 10-30% bränsle:\n• Accelerera mjukt\n• Höga växlar tidigt\n• Jämn hastighet\n• Stäng av motorn > 30 sek`,
      en: `Save 10-30% fuel:\n• Accelerate gently\n• High gears early\n• Steady speed\n• Turn off engine > 30 sec`
    }
  },
  {
    id: 28,
    category: 'eco',
    icon: Thermometer,
    color: 'bg-teal-600',
    title: { ar: 'الطقس والوقود', sv: 'Väder och bränsle', en: 'Weather & Fuel' },
    content: {
      ar: `الشتاء يزيد الاستهلاك:\n• لا تسخن طويلاً (30 ثانية)\n• ابدأ القيادة برفق\n\nالصيف:\n• المكيف يزيد 10-20%`,
      sv: `Vintern ökar förbrukning:\n• Värm inte länge (30 sek)\n• Börja köra försiktigt\n\nSommar:\n• AC ökar 10-20%`,
      en: `Winter increases consumption:\n• Don't warm long (30 sec)\n• Start driving gently\n\nSummer:\n• AC adds 10-20%`
    }
  },
  {
    id: 29,
    category: 'exam',
    icon: Eye,
    color: 'bg-indigo-500',
    title: { ar: 'أسئلة الصور', sv: 'Bildfrågor', en: 'Image Questions' },
    content: {
      ar: `ابحث عن:\n• جميع اللافتات\n• خطوط الأرض\n• السيارات والمشاة\n• الظروف (طقس، وقت)\n\nخذ وقتك في قراءة الصورة!`,
      sv: `Leta efter:\n• Alla skyltar\n• Vägmarkeringar\n• Bilar och fotgängare\n• Förhållanden (väder, tid)\n\nTa din tid att läsa bilden!`,
      en: `Look for:\n• All signs\n• Road markings\n• Cars and pedestrians\n• Conditions (weather, time)\n\nTake time to read the image!`
    }
  },
  {
    id: 30,
    category: 'exam',
    icon: AlertTriangle,
    color: 'bg-red-600',
    title: { ar: 'أخطاء شائعة', sv: 'Vanliga misstag', en: 'Common Mistakes' },
    content: {
      ar: `تجنب:\n• القراءة السريعة\n• تجاهل التفاصيل\n• "دائماً" و "أبداً" غالباً خاطئة\n\nغريزتك الأولى غالباً صحيحة!`,
      sv: `Undvik:\n• Snabb läsning\n• Ignorera detaljer\n• "Alltid" och "aldrig" ofta fel\n\nDin första instinkt är ofta rätt!`,
      en: `Avoid:\n• Speed reading\n• Ignoring details\n• "Always" and "never" often wrong\n\nFirst instinct is often right!`
    }
  },
  {
    id: 31,
    category: 'safety',
    icon: Moon,
    color: 'bg-slate-800',
    title: { ar: 'التعب والنعاس', sv: 'Trötthet', en: 'Fatigue' },
    content: {
      ar: `علامات التعب:\n• التثاؤب\n• عيون ثقيلة\n• الانحراف عن المسار\n\nالحل: توقف ونم 15-20 دقيقة\n\nكل ساعتين = استراحة 15 دقيقة`,
      sv: `Tecken på trötthet:\n• Gäspningar\n• Tunga ögon\n• Avviker från körfältet\n\nLösning: stanna och sov 15-20 min\n\nVar 2:a timme = 15 min paus`,
      en: `Fatigue signs:\n• Yawning\n• Heavy eyes\n• Drifting from lane\n\nSolution: stop and sleep 15-20 min\n\nEvery 2 hours = 15 min break`
    }
  },
  {
    id: 32,
    category: 'safety',
    icon: Car,
    color: 'bg-purple-600',
    title: { ar: 'نقل الحمولة', sv: 'Lastning', en: 'Loading' },
    content: {
      ar: `قواعد:\n• ثبت الحمولة جيداً\n• وزع الوزن بالتساوي\n• لا تحجب الرؤية\n\nالغرامة: 1200-4000 كرونة`,
      sv: `Regler:\n• Säkra lasten ordentligt\n• Fördela vikten jämnt\n• Blockera inte sikten\n\nBöter: 1200-4000 kr`,
      en: `Rules:\n• Secure load properly\n• Distribute weight evenly\n• Don't block view\n\nFine: 1200-4000 SEK`
    }
  },
  {
    id: 33,
    category: 'rules',
    icon: AlertTriangle,
    color: 'bg-yellow-600',
    title: { ar: 'ممر السكة الحديدية', sv: 'Järnvägsövergång', en: 'Railway Crossing' },
    content: {
      ar: `قواعد:\n• توقف عند الأحمر/الجرس\n• انتظر حتى يرتفع الحاجز\n• لا تتوقف على السكة!\n\nإذا علقت: أخرج الركاب فوراً!`,
      sv: `Regler:\n• Stanna vid rött/klocka\n• Vänta tills bommen är uppe\n• Stanna aldrig på spåret!\n\nOm du fastnar: evakuera direkt!`,
      en: `Rules:\n• Stop at red/bell\n• Wait until barrier is up\n• Never stop on track!\n\nIf stuck: evacuate immediately!`
    }
  },
  {
    id: 34,
    category: 'rules',
    icon: Truck,
    color: 'bg-amber-700',
    title: { ar: 'خلف المركبات الكبيرة', sv: 'Bakom stora fordon', en: 'Behind Large Vehicles' },
    content: {
      ar: `القاعدة الذهبية:\nإذا لم ترَ مرايا الشاحنة = السائق لا يراك!\n\nنصائح:\n• مسافة 4+ ثواني\n• لا تتجاوز من اليمين\n• انتبه عند الانعطافات`,
      sv: `Gyllene regeln:\nOm du inte ser lastbilens speglar = föraren ser inte dig!\n\nTips:\n• 4+ sekunders avstånd\n• Kör inte om från höger\n• Var uppmärksam vid svängar`,
      en: `Golden rule:\nIf you can't see truck's mirrors = driver can't see you!\n\nTips:\n• 4+ seconds distance\n• Don't pass from right\n• Watch at turns`
    }
  },
  {
    id: 35,
    category: 'parking',
    icon: Car,
    color: 'bg-blue-700',
    title: { ar: 'الركن الموازي', sv: 'Parallellparkering', en: 'Parallel Parking' },
    content: {
      ar: `الخطوات:\n1. مكان 1.5x أكبر من سيارتك\n2. توقف بجانب السيارة الأمامية\n3. أدر المقود يميناً وارجع\n4. أدر يساراً حتى تصبح موازياً\n5. اترك 30-50 سم من الرصيف`,
      sv: `Steg:\n1. Plats 1.5x större än din bil\n2. Stanna bredvid främre bilen\n3. Vrid ratten höger och backa\n4. Vrid vänster tills du är parallell\n5. Lämna 30-50 cm från trottoaren`,
      en: `Steps:\n1. Space 1.5x larger than your car\n2. Stop beside front car\n3. Turn wheel right and reverse\n4. Turn left until parallel\n5. Leave 30-50 cm from curb`
    }
  }
];
